
import { useEffect } from 'react';
import { CartProduct } from '@/contexts/CartContext';
import { saveToLocalStorage, getFromLocalStorage } from '@/utils/storage';
import { 
  addToCartSupabase,
  removeFromCartSupabase, 
  updateCartItemQuantitySupabase,
  clearCartSupabase,
  getCartItemsFromSupabase
} from '@/utils/cart/cartSupabaseUtils';

export const useCartSupabase = (
  userId: string,
  isAuthenticated: boolean,
  setCartItems: (items: CartProduct[] | ((prev: CartProduct[]) => CartProduct[])) => void,
  setIsLoading: (loading: boolean) => void,
  authLoading: boolean
) => {
  // Load cart from Supabase for authenticated users
  useEffect(() => {
    const loadCartFromSupabase = async () => {
      // Don't load anything while auth is still loading
      if (authLoading) {
        return;
      }
      
      if (isAuthenticated && userId !== 'guest') {
        setIsLoading(true);
        try {
          console.log(`Loading cart data from Supabase for authenticated user: ${userId}`);
          
          // Clear any localStorage data for this user to avoid conflicts
          localStorage.removeItem(`cart_${userId}`);
          localStorage.removeItem('cart_guest');
          sessionStorage.removeItem(`cart_cache_${userId}`);
          
          const supabaseCartItems = await getCartItemsFromSupabase(userId);
          
          if (supabaseCartItems.length > 0) {
            const formattedItems: CartProduct[] = supabaseCartItems.map(item => ({
              id: item.product_id,
              name: item.name,
              price: Number(item.price),
              image: item.image,
              quantity: item.quantity
            }));
            
            console.log(`Found ${formattedItems.length} items in Supabase cart`);
            setCartItems(formattedItems);
          } else {
            console.log('No items found in Supabase cart - setting empty cart');
            setCartItems([]);
          }
        } catch (error) {
          console.error('Error loading cart from Supabase:', error);
          // For authenticated users, always set empty cart on error rather than falling back to localStorage
          setCartItems([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        // For guest users, load from localStorage
        const localItems = getFromLocalStorage(`cart_guest`) || [];
        if (Array.isArray(localItems)) {
          setCartItems(localItems);
        } else {
          setCartItems([]);
        }
        setIsLoading(false);
      }
    };
    
    loadCartFromSupabase();
  }, [userId, isAuthenticated, setCartItems, setIsLoading, authLoading]);

  return {
    addToCartSupabase,
    removeFromCartSupabase,
    updateCartItemQuantitySupabase,
    clearCartSupabase
  };
};
